import { useMemo, useState } from "react";

import ListSubheader from "@material-ui/core/ListSubheader";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import AcUnitIcon from "@material-ui/icons/AcUnit";
import WhatshotIcon from "@material-ui/icons/Whatshot";
import ExposureIcon from "@material-ui/icons/Exposure";
import ImageIcon from "@material-ui/icons/Image";
import ToysIcon from "@material-ui/icons/Toys";
import OpacityIcon from "@material-ui/icons/Opacity";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import TextFormatIcon from "@material-ui/icons/TextFormat";

import { Apartment, DomoticsProvider, Room } from "../../../graphql/models";
import { TextField } from "@material-ui/core";

import useRoomDataEditorStyle from "./room_data_editor.style";
import { useSnackbarMessage, useSnackbarMutation } from "../../../hooks";
import { MUTATIONS } from "../../../graphql";
import { ImagePicker } from "..";

export interface RoomDataEditorOpts {
  apartment: Apartment;
  room: Room;
  onRoomUpdated?: (updatedRoom: Room) => void;
}

export function RoomDataEditor(props: RoomDataEditorOpts) {
  const { apartment, room, onRoomUpdated } = props;
  const classes = useRoomDataEditorStyle();

  const { raiseSuccessSnackbar } = useSnackbarMessage();

  const [invokeUpdateRoom] = useSnackbarMutation(MUTATIONS.UPDATE_ROOM, {
    onCompleted: () => {
      setIsReadOnly(true);
      if (onRoomUpdated) {
        onRoomUpdated({ ...roomData });
      }
      raiseSuccessSnackbar(`Informazioni stanza aggiornate`);
    },
  });

  const askWiserData = useMemo(() => {
    return apartment.domoticsProvider === DomoticsProvider.SCHNEIDER_ELECTRIC;
  }, [apartment]);

  const [isReadOnly, setIsReadOnly] = useState(true);
  const toggleEditMode = () => {
    if (isReadOnly) {
      setIsReadOnly(false);
    } else {
      invokeUpdateRoom({
        variables: {
          id: room.id,
          roomData: {
            ...roomData,
          },
        },
      });
    }
  };

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    if (!isReadOnly) {
      invokeUpdateRoom({
        variables: {
          id: room.id,
          roomData: {
            ...roomData,
          },
        },
      });
    }
  };

  const [name, setName] = useState<string>(room?.name ?? "");
  // Wiser opts
  const [getTemperatureAddress, setGetTemperatureAddress] = useState<
    string | undefined
  >(room?.wiserOpts?.getTemperatureAddress);
  const [getTemperatureAddressWinter, setGetTemperatureAddressWinter] =
    useState<string | undefined>(room?.wiserOpts?.getTemperatureAddressWinter);
  const [setTemperatureAddressWinter, setSetTemperatureAddressWinter] =
    useState<string | undefined>(room?.wiserOpts?.setTemperatureAddressWinter);
  const [getTemperatureAddressSummer, setGetTemperatureAddressSummer] =
    useState<string | undefined>(room?.wiserOpts?.getTemperatureAddressSummer);
  const [setTemperatureAddressSummer, setSetTemperatureAddressSummer] =
    useState<string | undefined>(room?.wiserOpts?.setTemperatureAddressSummer);
  const [getFanAddress, setGetFanAddress] = useState<string | undefined>(
    room?.wiserOpts?.getFanAddress
  );
  const [setFanAddress, setSetFanAddress] = useState<string | undefined>(
    room?.wiserOpts?.setFanAddress
  );
  const [getHumidityAddress, setGetHumidityAddress] = useState<
    string | undefined
  >(room?.wiserOpts?.getHumidityAddress);
  const [imageUrl, setImageUrl] = useState<string | undefined>(
    room?.image?.url ?? void 0
  );

  const roomData = useMemo(() => {
    const currentRoomData = {
      ...room,
    };
    delete currentRoomData.currentHumidity;
    delete currentRoomData.currentTemperature;
    delete currentRoomData.currentFan;
    delete currentRoomData.currentWinterTemperature;
    delete currentRoomData.currentSummerTemperature;

    return {
      ...currentRoomData,
      name,
      image: imageUrl
        ? {
            url: imageUrl,
          }
        : void 0,
      wiserOpts: {
        getTemperatureAddress,
        getTemperatureAddressSummer,
        setTemperatureAddressSummer,
        getTemperatureAddressWinter,
        setTemperatureAddressWinter,
        getFanAddress,
        setFanAddress,
        getHumidityAddress,
      },
    };
  }, [
    room,
    name,
    getTemperatureAddress,
    getTemperatureAddressSummer,
    setTemperatureAddressSummer,
    getTemperatureAddressWinter,
    setTemperatureAddressWinter,
    getFanAddress,
    setFanAddress,
    getHumidityAddress,
    imageUrl,
  ]);

  return (
    <form noValidate autoComplete="off" onSubmit={onFormSubmit}>
      <List
        component="nav"
        aria-labelledby={`room-${room?.id}-data-list-subheader`}
        subheader={
          <>
            <ListSubheader
              component="div"
              id={`room-${room?.id}-data-subheader`}
            >
              Proprietà stanza
              <ListItemSecondaryAction>
                <IconButton
                  onClick={toggleEditMode}
                  edge="end"
                  aria-label="edit"
                >
                  {isReadOnly ? <EditIcon /> : <SaveIcon />}
                </IconButton>
              </ListItemSecondaryAction>
            </ListSubheader>
          </>
        }
        style={{ width: "100%" }}
      >
        <>
          <ListItem>
            <ListItemIcon>
              <ImageIcon />
            </ListItemIcon>
            <ImagePicker
              disabled={isReadOnly}
              defaultValue={imageUrl}
              onUploaded={(entry) => setImageUrl(entry.url)}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <TextFormatIcon />
            </ListItemIcon>
            <TextField
              className={classes.textField}
              variant="outlined"
              defaultValue={name}
              onChange={(evt) => setName(evt.target.value)}
              disabled={isReadOnly}
              placeholder="Nome da visualizzare"
            />
          </ListItem>
          {askWiserData && (
            <>
              <ListSubheader component="li">
                Dati per elettronica WISER
              </ListSubheader>
              <ListItem>
                <ListItemIcon>
                  <ExposureIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={getTemperatureAddress}
                  onChange={(evt) => setGetTemperatureAddress(evt.target.value)}
                  disabled={isReadOnly}
                  label="Indirizzo GET temperatura"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AcUnitIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={getTemperatureAddressSummer}
                  onChange={(evt) =>
                    setGetTemperatureAddressSummer(evt.target.value)
                  }
                  disabled={isReadOnly}
                  label="Indirizzo GET temperatura estiva"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AcUnitIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={setTemperatureAddressSummer}
                  onChange={(evt) =>
                    setSetTemperatureAddressSummer(evt.target.value)
                  }
                  disabled={isReadOnly}
                  label="Indirizzo SET temperatura estiva"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WhatshotIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={getTemperatureAddressWinter}
                  onChange={(evt) =>
                    setGetTemperatureAddressWinter(evt.target.value)
                  }
                  disabled={isReadOnly}
                  label="Indirizzo GET temperatura invernale"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WhatshotIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={setTemperatureAddressWinter}
                  onChange={(evt) =>
                    setSetTemperatureAddressWinter(evt.target.value)
                  }
                  disabled={isReadOnly}
                  label="Indirizzo SET temperatura invernale"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ToysIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={getFanAddress}
                  onChange={(evt) => setGetFanAddress(evt.target.value)}
                  disabled={isReadOnly}
                  label="Indirizzo GET della ventola del clima della stanza"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ToysIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={setFanAddress}
                  onChange={(evt) => setSetFanAddress(evt.target.value)}
                  disabled={isReadOnly}
                  label="Indirizzo SET della ventola del clima della stanza"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <OpacityIcon />
                </ListItemIcon>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  defaultValue={getHumidityAddress}
                  onChange={(evt) => setGetHumidityAddress(evt.target.value)}
                  disabled={isReadOnly}
                  label="Indirizzo GET umidità stanza"
                />
              </ListItem>
            </>
          )}
        </>
      </List>
    </form>
  );
}
